import React, { useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from '../../Button';
import useForm from '../../../utils/useForm';
import TogglePasswordVisibilityButton from '../../forms/TogglePasswordVisibilityButton';
import ConfirmAction from '../../dialogs/ConfirmAction';
import { notifySuccess, notifyError } from '../../../utils/notify';
import { TrashIcon } from '@heroicons/react/outline';

const PatientEngagementAccountManagement = ({
  recipient: { pepUserName, pepEmail },
  sendResetPepLink,
  deletePEPUser,
  sendUserNamePEP,
}) => {
  const { t } = useTranslation();
  const usernameInputRef = useRef();
  const emailInputRef = useRef();

  const { renderField } = useForm({
    inputs: {
      username: {
        ref: usernameInputRef,
        label: t('forms:username.label'),
        type: 'password',
        defaultValue: pepUserName,
        readOnly: true,
        suffix: <TogglePasswordVisibilityButton inputRef={usernameInputRef} />,
        schema: {
          type: 'string',
          optional: true,
        },
      },
      email: {
        ref: emailInputRef,
        label: t('forms:email.label'),
        type: 'password',
        defaultValue: pepEmail,
        readOnly: true,
        suffix: <TogglePasswordVisibilityButton inputRef={emailInputRef} />,
        schema: {
          type: 'string',
          optional: true,
        },
      },
    },
  });

  const [isSending, setIsSending] = useState(false);
  const [isResetPassword, setResetPassword] = useState(true);
  const [isUserNameSent, setIsUserNameSent] = useState(false);
  const message = useMemo(() => {
    if (isResetPassword) {
      return t(
        'confirmations:accountManagement.sendPasswordResetEmail.message',
      );
    }
    if (isUserNameSent) {
      return t('confirmations:accountManagement.sendUsername.message');
    }
    return t('confirmations:accountManagement.deleteUser.message');
  }, [isResetPassword, isUserNameSent]);
  const [
    isConfirmSendPasswordResetEmailVisible,
    setIsConfirmSendPasswordResetEmailVisible,
  ] = useState(false);
  const handleOnOpenSendPasswordResetEmailDialog = () => {
    setResetPassword(true);
    setIsUserNameSent(false);
    setIsConfirmSendPasswordResetEmailVisible(true);
  };
  const handleOnOpenDeleteUserDialog = () => {
    setResetPassword(false);
    setIsUserNameSent(false);
    setIsConfirmSendPasswordResetEmailVisible(true);
  };
  const handleOnOpenSendUserNameDialog = () => {
    setResetPassword(false);
    setIsUserNameSent(true);
    setIsConfirmSendPasswordResetEmailVisible(true);
  };
  const handleOnCloseSendPasswordResetEmailDialog = () =>
    setIsConfirmSendPasswordResetEmailVisible(false);
  const handleOnSendPasswordResetEmail = () => {
    setIsSending(true);
    if (isResetPassword) {
      sendResetPepLink()
        .then(() => {
          setIsSending(false);
          handleOnCloseSendPasswordResetEmailDialog();
          notifySuccess(
            t('confirmations:accountManagement.sendPasswordResetEmail.success'),
          )();
        })
        .catch((err) => {
          setIsSending(false);
          handleOnCloseSendPasswordResetEmailDialog();
          // TECH-2730 F8. The method now refuses with the resolver's reason
          // ("No binding found", "Failed to create reset token"); show it,
          // as every other .catch(notifyError()) on this surface does, rather
          // than the fixed string that told the administrator nothing.
          notifyError()(
            err ||
              t('confirmations:accountManagement.sendPasswordResetEmail.error'),
          );
        });
    }
    if (isUserNameSent) {
      sendUserNamePEP()
        .then(() => {
          setIsSending(false);
          handleOnCloseSendPasswordResetEmailDialog();
          notifySuccess(
            t('confirmations:accountManagement.sendUsername.success'),
          )();
        })
        .catch((err) => {
          setIsSending(false);
          handleOnCloseSendPasswordResetEmailDialog();
          // Same as the reset path: the method now refuses with the
          // resolver's reason; show it rather than a fixed string.
          notifyError()(
            err || t('confirmations:accountManagement.sendUsername.error'),
          );
        });
    }
    if (!isResetPassword && !isUserNameSent) {
      deletePEPUser()
        .then(() => {
          setIsSending(false);
          handleOnCloseSendPasswordResetEmailDialog();
          notifySuccess(
            t('confirmations:accountManagement.deleteUser.success'),
          )();
        })
        .catch(() => {
          setIsSending(false);
          handleOnCloseSendPasswordResetEmailDialog();
          notifyError()(t('confirmations:accountManagement.deleteUser.error'));
        });
    }
  };

  return (
    <div className="stack-6">
      <h2 className="capitalize">{t('userAccountManagement')}</h2>
      <div className="grid grid-cols-5 gap-5 items-end">
        {renderField('username')}
        {renderField('email')}
        <Button
          icon={<TrashIcon />}
          onClick={handleOnOpenDeleteUserDialog}
          disabled={!pepUserName}
        >
          {t('deleteUser')}
        </Button>
        <Button
          onClick={handleOnOpenSendPasswordResetEmailDialog}
          disabled={!pepUserName}
        >
          {t('sendPasswordResetEmail')}
        </Button>
        <Button
          onClick={handleOnOpenSendUserNameDialog}
          disabled={!pepUserName}
        >
          {t('sendUserName')}
        </Button>
        <ConfirmAction
          message={message}
          open={isConfirmSendPasswordResetEmailVisible}
          onConfirm={handleOnSendPasswordResetEmail}
          onCancel={handleOnCloseSendPasswordResetEmailDialog}
          loading={isSending}
        />
      </div>
    </div>
  );
};

PatientEngagementAccountManagement.propTypes = {
  recipient: PropTypes.shape({
    pepUserName: PropTypes.string,
    pepEmail: PropTypes.string,
  }).isRequired,
  sendResetPepLink: PropTypes.func.isRequired,
  deletePEPUser: PropTypes.func.isRequired,
  sendUserNamePEP: PropTypes.func.isRequired,
};

export default PatientEngagementAccountManagement;
